// Shared one-line summaries for a filter condition's value: a lane condition
// (perspective / relationship / arc), used by both `EditableFilterRowMolecule`
// (a lane condition authored as a `type==='...'` property row) and
// `LaneFilterRowMolecule` (the same condition held as a `FilterGroupLeaf` with
// a `kind` discriminant), and a property row's value read-back, used by
// `EditableFilterRowMolecule`'s closed summary and the tree's "Reads as"
// sentence (`cfGroup.ts` `readsAsPredicate`). Extracted so no two surfaces
// drift on how a condition reads back to the user — see spec
// unified-filters-v2.md Reuse Audit ("LaneFilterRowMolecule").
import type { ArcFilter, FilterExpression, PerspectiveFilter, RelationshipFilter } from 'types/filters';
import { readBetweenBounds, RELATIVE_DAYS_OPERATORS, VALUELESS_OPERATORS, type TranslateFn } from './filterOperators';
import { buildFilterArcOptions, prettifyKey } from './filterPropertyBuilder';
import { humanizeRollingDateToken } from './rollingDateLabels';
import type { PropertyOption } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';
import type { RelationshipOption } from '../components/molecules/RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../components/molecules/PerspectiveFilter/PerspectiveFilterMolecule';

/**
 * Resolves a lane condition's `systemName` to its human label via the injected
 * catalog. The summary is user-facing, so it must never print the raw canonical
 * token (`center_responsible`) — same rule the operator label already follows.
 * Falls back to the system name when the catalog isn't supplied (the row still
 * renders identifiably rather than blank).
 */
export function labelForSystemName(
  systemName: string,
  options?: { systemName: string; label: string }[]
): string {
  return options?.find((o) => o.systemName === systemName)?.label ?? systemName;
}

/**
 * The localized labels of the arc lane's two closed axes, keyed by value —
 * taken from `buildFilterArcOptions`, the same source the arc value editor's
 * chips use, so the summary and the editor can never name a value differently.
 * `ArcHealthStatus` is localized under `arc.health.*` (types/filters.d.ts), not
 * the sphere-status map `statusPopover.statusValues.*`, which has no `overdue`
 * or `attention_needed` and so printed the raw key for both.
 */
function arcAxisLabels(t: TranslateFn): { health: Map<string, string>; lifecycle: Map<string, string> } {
  const { healthOptions, lifecycleOptions } = buildFilterArcOptions({
    t: (key, opts) => t(key, { defaultValue: opts?.defaultValue }),
  });
  return {
    health: new Map(healthOptions.map((o) => [o.value, o.label])),
    lifecycle: new Map(lifecycleOptions.map((o) => [o.value, o.label])),
  };
}

/**
 * A stored axis value outside the canonical set (a value persisted by an older
 * build, or written outside the editor) still reads as words — never as a raw
 * i18n key, and never dropped, since the engine still filters on it.
 */
const axisLabel = (labels: Map<string, string>, value: string): string => labels.get(value) ?? prettifyKey(value);

/** Arc rows carry no meaningful operator/value pair for the summary — describe the ArcFilter instead. */
export function summarizeArcFilter(af: ArcFilter, t: TranslateFn): string {
  const bits: string[] = [
    af.arcSelector === 'primary'
      ? t('filterEditor.row.arcSelector.primary')
      : t('filterEditor.row.arcSelector.any'),
  ];
  // Health and lifecycle are closed canonical unions with label maps in both
  // locales; the summary is user-facing, so they go through `t()` rather than
  // printing `on_track`/`not_started` raw (the contract on `labelForSystemName`
  // above). A stage id is a template-specific opaque id with no label available
  // here, so it is COUNTED rather than printed — never a raw id.
  const labels = af.healthStatus?.length || af.lifecycle?.length ? arcAxisLabels(t) : undefined;
  if (labels && af.healthStatus?.length) {
    bits.push(af.healthStatus.map((h) => axisLabel(labels.health, h)).join('/'));
  }
  if (af.stageId?.length) {
    bits.push(t('filterEditor.row.arcStageCount', { count: af.stageId.length }));
  }
  if (labels && af.lifecycle?.length) {
    bits.push(af.lifecycle.map((l) => axisLabel(labels.lifecycle, l)).join('/'));
  }
  return bits.join(' · ');
}

/** Relationship rows carry no meaningful operator/value pair for the summary — describe the RelationshipFilter instead. */
export function summarizeRelationshipFilter(
  rf: RelationshipFilter,
  t: TranslateFn,
  availableRelationships?: RelationshipOption[]
): string {
  const bits: string[] = [];
  if (rf.operator === 'not_in') bits.push(t('filterEditor.row.relationshipNot'));
  if (rf.relationshipSystemName) {
    bits.push(labelForSystemName(rf.relationshipSystemName, availableRelationships));
  }
  const relatedCount = (rf.relatedTo ?? []).length;
  if (relatedCount > 0) bits.push(t('filterEditor.row.relatedSphereCount', { count: relatedCount }));
  return bits.join(' · ');
}

/** Perspective rows carry no meaningful operator/value pair for the summary — describe the PerspectiveFilter instead. */
export function summarizePerspectiveFilter(
  pf: PerspectiveFilter,
  t: TranslateFn,
  availablePerspectives?: PerspectiveOption[]
): string {
  const bits: string[] = [];
  if (pf.operator === 'not_in') bits.push(t('filterEditor.row.perspectiveNot'));
  if (pf.perspectiveSystemName) {
    bits.push(labelForSystemName(pf.perspectiveSystemName, availablePerspectives));
  }
  const assigneeCount = (pf.assignees ?? []).length;
  if (assigneeCount > 0) bits.push(t('filterEditor.row.assigneeCount', { count: assigneeCount }));
  return bits.join(' · ');
}

/** The lane catalogs a property row of a lane pseudo-type resolves its value's names through. */
export interface FilterValueLaneCatalogs {
  availableRelationships?: RelationshipOption[];
  availablePerspectives?: PerspectiveOption[];
}

/**
 * A relative-days value (`within_last_days` / `within_next_days`) as its day
 * count, or `undefined` when nothing usable is entered. The operator label
 * carries no number ("In the last"), so the count is the only place it lives.
 */
export function relativeDayCount(filter: FilterExpression): number | undefined {
  const raw = filter.value;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

const DATE_TYPES = new Set<PropertyOption['type']>(['date', 'datetime']);
const LABELLED_SET_TYPES = new Set<PropertyOption['type']>(['enum', 'multi', 'person']);

/** A stored date operand as epoch ms — a number as itself, an ISO string parsed, anything else `NaN`. */
function parseDateMs(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') return Date.parse(raw);
  return Number.NaN;
}

/**
 * A date / datetime row's stored value, read back in the author's locale and
 * time zone (the calendar picked a local day, so a UTC rendering could show the
 * day before).
 *
 * A rolling-date token NEVER reads back as itself: `@startOfQuarter-1q` is a
 * wire format, and a reader shown the raw string has to know the grammar to
 * know what their own filter selects. It reads as the words
 * `humanizeRollingDateToken` gives it — which is the same phrase the preset
 * control shows, because both call that one function. `@today` keeps reading
 * exactly as it did ("Today"), so no already-saved filter changes what it says.
 *
 * Anything unparseable still reads as written — a malformed token must look
 * wrong, not be dressed up as a date.
 */
function formatDateValue(raw: unknown, type: 'date' | 'datetime', t: TranslateFn, locale?: string): string {
  const rolling = humanizeRollingDateToken(raw, t);
  if (rolling !== null) return rolling;
  const ms = parseDateMs(raw);
  if (Number.isNaN(ms)) return String(raw);
  const format = new Intl.DateTimeFormat(
    locale,
    type === 'date' ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' }
  );
  return format.format(new Date(ms));
}

/** `filter.operator === 'between'`'s summary — the two bounds, formatted as dates when the property is one. */
function summarizeBetween(
  filter: FilterExpression,
  dateType: 'date' | 'datetime' | undefined,
  t: TranslateFn,
  locale?: string
): string {
  // `min`/`max` are the canonical bounds — numbers, or an ISO date string.
  const { min, max } = readBetweenBounds(filter);
  if (min === undefined || max === undefined) return '';
  const format = (bound: number | string) => (dateType ? formatDateValue(bound, dateType, t, locale) : String(bound));
  return t('filterEditor.row.betweenSummary', { min: format(min), max: format(max) });
}

// `multi`/`person` carry the same `enumValues` label lookup `enum` does;
// `spheretype`/`spherefamily` resolve through the schema catalog instead
// (`SphereTypeSelectMolecule`'s own chip), not the plain `enumValues` shape,
// so they fall through to the raw-value join below.
const enumOrRawLabel = (v: unknown, prop?: PropertyOption): string =>
  prop && LABELLED_SET_TYPES.has(prop.type) && prop.enumValues
    ? (prop.enumValues.find((ev) => ev.value === v)?.label ?? String(v))
    : String(v);

/** The plain (non-`between`, non-relative-days, non-lane) operand — an enum/date/raw value or a joined list. */
function summarizeRawValue(
  filter: FilterExpression,
  prop: PropertyOption | undefined,
  dateType: 'date' | 'datetime' | undefined,
  t: TranslateFn,
  locale?: string
): string {
  const raw = Array.isArray(filter.values) && filter.values.length > 0 ? filter.values : filter.value;
  if (raw === undefined || raw === null || raw === '') return '';
  if (Array.isArray(raw)) {
    return raw.map((v) => enumOrRawLabel(v, prop)).join(', ');
  }
  if (dateType) return formatDateValue(raw, dateType, t, locale);
  return enumOrRawLabel(raw, prop);
}

/**
 * A property row's value, as the words a reader sees after the operator —
 * "Urgent, High", "Sep 15, 2026", "7 days", "3 to 9". Empty when the operator
 * takes no value or none is entered yet. A lane pseudo-type (`arc` /
 * `relationship` / `perspective` authored as a property row) reads through its
 * lane's own summary instead.
 */
export function summarizeFilterValue(
  filter: FilterExpression,
  t: TranslateFn,
  prop?: PropertyOption,
  laneCatalogs?: FilterValueLaneCatalogs,
  locale?: string
): string {
  if (prop?.type === 'arc') return summarizeArcFilter((filter.value as ArcFilter) ?? { operator: 'in' }, t);
  if (prop?.type === 'relationship') {
    return summarizeRelationshipFilter(
      (filter.value as RelationshipFilter) ?? { relationshipSystemName: '', operator: 'in', relatedTo: [] },
      t,
      laneCatalogs?.availableRelationships
    );
  }
  if (prop?.type === 'perspective') {
    return summarizePerspectiveFilter(
      (filter.value as PerspectiveFilter) ?? { perspectiveSystemName: '', operator: 'in', assignees: [] },
      t,
      laneCatalogs?.availablePerspectives
    );
  }
  if (VALUELESS_OPERATORS.includes(filter.operator)) return '';
  const dateType = prop && DATE_TYPES.has(prop.type) ? (prop.type as 'date' | 'datetime') : undefined;
  if (RELATIVE_DAYS_OPERATORS.includes(filter.operator)) {
    const count = relativeDayCount(filter);
    return count === undefined ? '' : t('filterEditor.row.relativeDaysSummary', { count });
  }
  if (filter.operator === 'between') return summarizeBetween(filter, dateType, t, locale);
  return summarizeRawValue(filter, prop, dateType, t, locale);
}
